/*
 * Is anything stopping this sequence from sending?
 *
 * The status rail already answers this in detail, but only on the
 * Configurations tab -- so a user can sit on Overview reading "contacts are
 * being scheduled" next to an Active toggle while nothing has sent for weeks.
 * This computes only the blocking subset, from the same sources the rail uses,
 * so the two can never contradict each other.
 *
 * Returns the single worst blocker, or null. One line, one action -- a list of
 * problems is a thing to ignore.
 */

const EMAIL_TYPES = ["email", "manualEmail"];

// Which channels this sequence actually uses. Lives here rather than in
// SequenceStatusRail so both can share it without dragging the rail's UI
// dependencies into anything that only needs the rules.
export const stepVariantTypes = (steps = {}) => {
  const types = new Set();
  Object.values(steps || {}).forEach(step => {
    Object.values(step?.variants || {}).forEach(variant => {
      variant?.type && types.add(variant.type);
    });
  });
  return types;
};

export const sequenceBlockers = ({
  sequence = {},
  steps = {},
  healthyInboxes = [],
  workspaceInboxes = [],
  liAccounts = [],
  waiting = 0
} = {}) => {
  const status = (sequence?.status || "").toLowerCase();
  const types = stepVariantTypes(steps);
  const hasEmailSteps = EMAIL_TYPES.some(type => types.has(type));
  const hasLiAutoSteps = types.has("linkedinAuto");
  const stepCount = Object.keys(steps || {}).length;
  // "Waiting behind this" is what makes a blocker urgent rather than tidy.
  const behind = Number(waiting) || 0;
  const behindText = behind
    ? ` ${behind} contact${behind === 1 ? " is" : "s are"} waiting behind it.`
    : "";

  if (!stepCount) {
    return {
      severity: "bad",
      code: "no_steps",
      title: "This sequence has no steps.",
      text: "Nothing will send until you add at least one.",
      actionLabel: "Add a step"
    };
  }

  if (hasEmailSteps && !healthyInboxes.length) {
    const hasWorkspaceInboxes = (workspaceInboxes || []).some(
      inbox => inbox?.status === "ACTIVE"
    );
    return {
      severity: "bad",
      code: hasWorkspaceInboxes ? "no_inbox_assigned" : "no_inbox_connected",
      title: "This sequence cannot send.",
      text: hasWorkspaceInboxes
        ? `It has email steps but no inbox assigned to it.${behindText} Your workspace already has inboxes available.`
        : `It has email steps and no inbox is connected to your workspace.${behindText}`,
      actionLabel: hasWorkspaceInboxes ? "Assign an inbox" : "Connect an inbox"
    };
  }

  if (hasLiAutoSteps && !(liAccounts || []).length) {
    return {
      severity: "bad",
      code: "no_linkedin",
      title: "LinkedIn steps will not run.",
      text: `This sequence has automatic LinkedIn steps but no LinkedIn account is connected.${behindText}`,
      actionLabel: "Connect LinkedIn"
    };
  }

  // Deliberate states, reported without alarm -- the user chose these.
  if (status === "paused") {
    return {
      severity: "warn",
      code: "paused",
      title: "This sequence is paused.",
      text: `Nothing is sending while it stays paused.${behindText}`,
      actionLabel: "Resume it"
    };
  }

  if (status === "draft") {
    return {
      severity: "warn",
      code: "draft",
      title: "This sequence has never been activated.",
      text: "It will not send anything until you turn it on.",
      actionLabel: "Activate it"
    };
  }

  return null;
};

export default sequenceBlockers;
